/**
 * Custom Export Builder — multi-dataset, split-by-team, saved views.
 * Datasets: leads, students, users, attendance.
 * For each dataset we conditionally render the relevant filter chips.
 * Includes a "Saved Views" sidebar so a user can store + re-run filter combos.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Wand2, Loader2, X, ChevronDown, Sparkles, Save, Star, Play, Trash2,
  Users as UsersIcon, GraduationCap, Phone, CalendarCheck, Layers,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DATASETS = [
  { value: 'leads',      label: 'Leads (Sales)',     icon: Phone,         description: 'CRM leads — all sources, stages, agents' },
  { value: 'students',   label: 'Students (CS)',     icon: GraduationCap, description: 'Enrolled students — CS, mentor, BD stages' },
  { value: 'users',      label: 'Users',             icon: UsersIcon,     description: 'ERP user accounts — role, dept, team' },
  { value: 'attendance', label: 'HR Attendance',     icon: CalendarCheck, description: 'Daily attendance — by date / dept / status' },
];

const titleCase = (s) =>
  (s || '').toString().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const MultiSelect = ({ label, options, value, onChange, testId, placeholder = 'Any' }) => {
  const selected = new Set(value || []);
  const [query, setQuery] = useState('');
  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(Array.from(next));
  };
  const labelText = selected.size === 0
    ? placeholder
    : selected.size === 1
      ? (options.find((o) => o.value === [...selected][0])?.label || [...selected][0])
      : `${selected.size} selected`;
  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [options, query]);
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between font-normal"
            data-testid={`ms-${testId}`}
          >
            <span className={selected.size === 0 ? 'text-muted-foreground' : ''}>{labelText}</span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          {options.length > 8 && (
            <div className="p-2 border-b sticky top-0 bg-background z-10">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}…`}
                className="h-8 text-sm"
                data-testid={`ms-${testId}-search`}
              />
            </div>
          )}
          <div
            className="overflow-y-auto overscroll-contain"
            style={{ maxHeight: '20rem' }}
            data-testid={`ms-${testId}-list`}
          >
            <div className="p-2 space-y-1">
              {selected.size > 0 && (
                <Button variant="ghost" size="sm" className="w-full text-xs justify-start text-muted-foreground"
                  onClick={() => onChange([])} data-testid={`ms-${testId}-clear`}>
                  <X className="h-3 w-3 mr-1" /> Clear ({selected.size})
                </Button>
              )}
              {filteredOptions.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {query ? `No matches for "${query}"` : 'No options'}
                </p>
              )}
              {filteredOptions.map((opt) => (
                <label key={opt.value}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded cursor-pointer text-sm">
                  <Checkbox checked={selected.has(opt.value)} onCheckedChange={() => toggle(opt.value)} />
                  <span className="flex-1 truncate">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
          {options.length > 8 && (
            <div className="px-2 py-1.5 border-t text-[10px] text-muted-foreground bg-muted/30">
              Showing {filteredOptions.length} of {options.length}
              {selected.size > 0 && ` · ${selected.size} selected`}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

const initialFilters = () => ({
  search: '', team_id: [], department: [], assigned_to: [], previous_assigned_to: [],
  stage: [], lead_source: [], role: [], is_active: '',
  mentor_stage: [], bd_stage: [],
  employee_id: [], status: [],
  date_field: 'created_at', date_from: '', date_to: '',
});

const dateFieldOptionsFor = (dataset) => {
  if (dataset === 'leads')      return [['created_at','Created at'],['assigned_at','Assigned at'],['last_activity_at','Last activity'],['updated_at','Updated at']];
  if (dataset === 'students')   return [['enrolled_at','Enrolled at'],['created_at','Created at'],['last_activity','Last activity'],['updated_at','Updated at']];
  if (dataset === 'users')      return [['created_at','Created at'],['updated_at','Updated at'],['last_login_at','Last login']];
  if (dataset === 'attendance') return [['date','Attendance date'],['created_at','Created at']];
  return [['created_at','Created at']];
};

const defaultDateField = (dataset) => dateFieldOptionsFor(dataset)[0][0];

const CustomExportBuilder = ({ onGenerated }) => {
  const [dataset, setDataset] = useState('leads');
  const [filters, setFilters] = useState(initialFilters());
  const [splitByTeam, setSplitByTeam] = useState(false);
  const [filenamePrefix, setFilenamePrefix] = useState('custom_leads');
  const [opts, setOpts] = useState({});
  const [running, setRunning] = useState(false);
  const [savedViews, setSavedViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);

  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  const loadOpts = useCallback(async () => {
    try {
      const res = await api.get(`/admin/exports/_filter_options/${dataset}`);
      setOpts(res.data || {});
    } catch {
      toast.error(`Failed to load filter options for ${dataset}`);
    }
  }, [dataset]);

  const loadViews = useCallback(async () => {
    try {
      const res = await api.get('/admin/exports/saved-views');
      setSavedViews(res.data?.views || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadOpts(); }, [loadOpts]);
  useEffect(() => { loadViews(); }, [loadViews]);

  // When dataset changes, reset filters + sane defaults
  useEffect(() => {
    setFilters((prev) => ({ ...initialFilters(), date_field: defaultDateField(dataset), search: prev.search }));
    setFilenamePrefix(`custom_${dataset}`);
    setSplitByTeam(false);
  }, [dataset]);

  const filtersCount = useMemo(() => {
    let c = 0;
    if (filters.search) c++;
    ['team_id','department','assigned_to','previous_assigned_to','stage','lead_source',
     'role','mentor_stage','bd_stage','employee_id','status'].forEach((k) => {
      if (Array.isArray(filters[k]) && filters[k].length > 0) c++;
    });
    if (filters.date_from) c++;
    if (filters.date_to) c++;
    if (filters.is_active !== '' && filters.is_active !== null && filters.is_active !== undefined) c++;
    return c;
  }, [filters]);

  // Option builders
  const teamOptions = useMemo(() => (opts.teams || []).map((t) => ({ value: t.id, label: t.name })), [opts.teams]);
  const deptOptions = useMemo(() => (opts.departments || []).map((d) => ({ value: d, label: d })), [opts.departments]);
  const userOptions = useMemo(() => (opts.users || []).map((u) => ({
    value: u.id, label: `${u.full_name || u.email}${u.role ? ` · ${titleCase(u.role)}` : ''}`,
  })), [opts.users]);
  const stageOptions   = useMemo(() => (opts.stages || []).map((s) => ({ value: s, label: titleCase(s) })), [opts.stages]);
  const sourceOptions  = useMemo(() => (opts.lead_sources || []).map((s) => ({ value: s, label: titleCase(s) })), [opts.lead_sources]);
  const mentorStageOpts = useMemo(() => (opts.mentor_stages || []).map((s) => ({ value: s, label: titleCase(s) })), [opts.mentor_stages]);
  const bdStageOpts     = useMemo(() => (opts.bd_stages || []).map((s) => ({ value: s, label: titleCase(s) })), [opts.bd_stages]);
  const roleOptions     = useMemo(() => (opts.roles || []).map((r) => ({ value: r, label: titleCase(r) })), [opts.roles]);
  const employeeOptions = useMemo(() => (opts.employees || []).map((e) => ({
    value: e.id, label: `${e.full_name} (${e.employee_id})${e.department ? ` · ${e.department}` : ''}`,
  })), [opts.employees]);
  const attStatusOpts   = useMemo(() => (opts.statuses || []).map((s) => ({ value: s, label: titleCase(s) })), [opts.statuses]);

  const reset = () => {
    setFilters({ ...initialFilters(), date_field: defaultDateField(dataset) });
    setSplitByTeam(false);
    setFilenamePrefix(`custom_${dataset}`);
  };

  const downloadFile = async (filename) => {
    const token = sessionStorage.getItem('clt_token');
    const res = await fetch(`${BACKEND_URL}/api/admin/exports/${encodeURIComponent(filename)}`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const buildPayload = () => {
    const cleaned = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) return;
      if (Array.isArray(v) && v.length === 0) return;
      cleaned[k] = v;
    });
    return {
      dataset, filename_prefix: filenamePrefix || `custom_${dataset}`,
      split_by_team: splitByTeam,
      filters: cleaned,
    };
  };

  const generate = async () => {
    setRunning(true);
    const tId = toast.loading(splitByTeam ? 'Building per-team Excel zip…' : 'Building your custom Excel…');
    try {
      const res = await api.post('/admin/exports/custom', buildPayload());
      const { filename, rows, size_human, sheet_count } = res.data || {};
      toast.success(
        splitByTeam
          ? `Generated ${filename} — ${rows} rows across ${sheet_count} teams, ${size_human}`
          : `Generated ${filename} — ${rows} rows, ${size_human}`,
        { id: tId },
      );
      await downloadFile(filename);
      onGenerated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Custom export failed', { id: tId });
    } finally {
      setRunning(false);
    }
  };

  const saveCurrent = async () => {
    if (!saveName.trim()) { toast.error('Give the view a name'); return; }
    try {
      await api.post('/admin/exports/saved-views', {
        ...buildPayload(), name: saveName.trim(), shared: saveShared,
      });
      toast.success(`Saved "${saveName}"`);
      setSaveOpen(false); setSaveName(''); setSaveShared(false);
      loadViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const runView = async (view) => {
    setRunning(true);
    const tId = toast.loading(`Running "${view.name}"…`);
    try {
      const res = await api.post(`/admin/exports/saved-views/${view.id}/run`);
      const { filename, rows, size_human } = res.data || {};
      toast.success(`Done — ${rows} rows, ${size_human}`, { id: tId });
      await downloadFile(filename);
      loadViews();
      onGenerated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed', { id: tId });
    } finally {
      setRunning(false);
    }
  };

  const loadView = (view) => {
    setDataset(view.dataset);
    // dataset effect resets filters; setting them in next tick:
    setTimeout(() => {
      setFilters({ ...initialFilters(), ...(view.filters || {}), date_field: view.filters?.date_field || defaultDateField(view.dataset) });
      setSplitByTeam(!!view.split_by_team);
      setFilenamePrefix(view.filename_prefix || `custom_${view.dataset}`);
      toast.info(`Loaded "${view.name}" — edit & re-run, or click Generate`);
    }, 0);
  };

  const deleteView = async (view) => {
    if (!window.confirm(`Delete saved view "${view.name}"?`)) return;
    try {
      await api.delete(`/admin/exports/saved-views/${view.id}`);
      toast.success('Deleted');
      loadViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const renderDatasetFilters = () => {
    const common = (
      <>
        <MultiSelect label="Team(s)" options={teamOptions} value={filters.team_id} onChange={(v) => setF('team_id', v)} testId="team" />
        <MultiSelect label="Department(s)" options={deptOptions} value={filters.department} onChange={(v) => setF('department', v)} testId="dept" />
      </>
    );
    if (dataset === 'leads') {
      return (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {common}
            <MultiSelect label="Stage(s)" options={stageOptions} value={filters.stage} onChange={(v) => setF('stage', v)} testId="stage" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <MultiSelect label="Currently assigned to" options={userOptions} value={filters.assigned_to} onChange={(v) => setF('assigned_to', v)} testId="assigned-to" placeholder="Any agent" />
            <MultiSelect label="Previously assigned to" options={userOptions} value={filters.previous_assigned_to} onChange={(v) => setF('previous_assigned_to', v)} testId="prev-assigned-to" placeholder="Any prior agent" />
            <MultiSelect label="Lead source(s)" options={sourceOptions} value={filters.lead_source} onChange={(v) => setF('lead_source', v)} testId="source" />
          </div>
        </>
      );
    }
    if (dataset === 'students') {
      return (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {common}
            <MultiSelect label="CS Stage(s)" options={stageOptions} value={filters.stage} onChange={(v) => setF('stage', v)} testId="stage" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <MultiSelect label="Assigned (CS/Mentor/BD)" options={userOptions} value={filters.assigned_to} onChange={(v) => setF('assigned_to', v)} testId="assigned-to" />
            <MultiSelect label="Mentor stage" options={mentorStageOpts} value={filters.mentor_stage} onChange={(v) => setF('mentor_stage', v)} testId="mentor-stage" />
            <MultiSelect label="BD stage" options={bdStageOpts} value={filters.bd_stage} onChange={(v) => setF('bd_stage', v)} testId="bd-stage" />
          </div>
        </>
      );
    }
    if (dataset === 'users') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <MultiSelect label="Role(s)" options={roleOptions} value={filters.role} onChange={(v) => setF('role', v)} testId="role" />
          <MultiSelect label="Department(s)" options={deptOptions} value={filters.department} onChange={(v) => setF('department', v)} testId="dept" />
          <MultiSelect label="Team(s)" options={teamOptions} value={filters.team_id} onChange={(v) => setF('team_id', v)} testId="team" />
          <div>
            <Label className="text-xs">Active</Label>
            <Select value={filters.is_active === '' ? '_any' : String(filters.is_active)}
              onValueChange={(v) => setF('is_active', v === '_any' ? '' : v === 'true')}>
              <SelectTrigger data-testid="users-active"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_any">Any</SelectItem>
                <SelectItem value="true">Active only</SelectItem>
                <SelectItem value="false">Inactive only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }
    if (dataset === 'attendance') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <MultiSelect label="Employee(s)" options={employeeOptions} value={filters.employee_id} onChange={(v) => setF('employee_id', v)} testId="employee" placeholder="Any employee" />
          <MultiSelect label="Department(s)" options={deptOptions} value={filters.department} onChange={(v) => setF('department', v)} testId="dept" />
          <MultiSelect label="Status(es)" options={attStatusOpts} value={filters.status} onChange={(v) => setF('status', v)} testId="att-status" />
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="border-violet-200 dark:border-violet-900/50" data-testid="custom-export-builder">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-violet-600" />
              Custom Export Builder
            </CardTitle>
            <CardDescription>
              Build any export — pick a dataset, combine filters, split by team if needed, and save the recipe.
            </CardDescription>
          </div>
          {filtersCount > 0 && (
            <Badge variant="secondary" className="bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300">
              {filtersCount} filter{filtersCount > 1 ? 's' : ''} active
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Dataset picker */}
        <div>
          <Label className="text-xs">Dataset</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {DATASETS.map(({ value, label, icon: Icon, description }) => (
              <button
                key={value}
                type="button"
                onClick={() => setDataset(value)}
                data-testid={`dataset-${value}`}
                className={`text-left rounded-lg border-2 p-3 transition-all ${
                  dataset === value
                    ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30'
                    : 'border-border hover:border-violet-300'
                }`}
              >
                <div className="flex items-center gap-2 font-medium text-sm">
                  <Icon className={`h-4 w-4 ${dataset === value ? 'text-violet-600' : 'text-muted-foreground'}`} />
                  {label}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div>
          <Label className="text-xs">Search</Label>
          <Input
            value={filters.search}
            onChange={(e) => setF('search', e.target.value)}
            placeholder={
              dataset === 'attendance' ? 'employee name or code'
              : 'name / phone / email partial'
            }
            data-testid="custom-export-search"
          />
        </div>

        {renderDatasetFilters()}

        {/* Date range */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Date field</Label>
            <Select value={filters.date_field} onValueChange={(v) => setF('date_field', v)}>
              <SelectTrigger data-testid="custom-export-date-field"><SelectValue /></SelectTrigger>
              <SelectContent>
                {dateFieldOptionsFor(dataset).map(([v, lbl]) => (
                  <SelectItem key={v} value={v}>{lbl}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setF('date_from', e.target.value)} data-testid="custom-export-date-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setF('date_to', e.target.value)} data-testid="custom-export-date-to" />
          </div>
          <div>
            <Label className="text-xs">Filename prefix</Label>
            <Input value={filenamePrefix} onChange={(e) => setFilenamePrefix(e.target.value)} data-testid="custom-export-prefix" />
          </div>
        </div>

        {/* Split by team toggle */}
        <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3 border">
          <div className="flex items-start gap-3">
            <Layers className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Split output by Team</p>
              <p className="text-xs text-muted-foreground">
                Produces one .xlsx per team inside a single .zip file. Useful for distributing per-team reports.
              </p>
            </div>
          </div>
          <Checkbox
            checked={splitByTeam}
            onCheckedChange={(v) => setSplitByTeam(!!v)}
            data-testid="split-by-team-toggle"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-2 flex-wrap">
          <Button variant="ghost" onClick={reset} disabled={running} data-testid="custom-export-reset">Reset</Button>
          <Button
            variant="outline"
            onClick={() => { setSaveName(''); setSaveShared(false); setSaveOpen(true); }}
            disabled={running}
            data-testid="custom-export-save"
          >
            <Save className="h-4 w-4 mr-2" />
            Save as view
          </Button>
          <Button onClick={generate} disabled={running} className="bg-violet-600 hover:bg-violet-700" data-testid="custom-export-generate">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
            Generate &amp; Download
          </Button>
        </div>

        {/* Saved Views */}
        {savedViews.length > 0 && (
          <div className="border-t pt-4 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
              <p className="text-sm font-medium">Saved Views</p>
              <Badge variant="secondary">{savedViews.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {savedViews.map((v) => (
                <div key={v.id}
                  className="border rounded-lg p-3 flex items-start justify-between gap-2 hover:border-violet-300 transition"
                  data-testid={`saved-view-${v.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium truncate">{v.name}</p>
                      <Badge variant="outline" className="text-[10px] py-0">{v.dataset}</Badge>
                      {v.split_by_team && <Badge variant="outline" className="text-[10px] py-0 text-amber-700 border-amber-400">split</Badge>}
                      {v.shared && <Badge variant="outline" className="text-[10px] py-0 text-violet-700 border-violet-400">shared</Badge>}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {v.user_name && <>by {v.user_name} · </>}
                      ran {v.run_count || 0} time{v.run_count !== 1 ? 's' : ''}
                      {v.last_run_at && <> · last {new Date(v.last_run_at).toLocaleDateString()}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => loadView(v)}
                      title="Load filters"
                      data-testid={`saved-view-load-${v.id}`}
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm" variant="ghost" className="text-violet-700"
                      onClick={() => runView(v)} disabled={running}
                      title="Run & download"
                      data-testid={`saved-view-run-${v.id}`}
                    >
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700"
                      onClick={() => deleteView(v)}
                      title="Delete"
                      data-testid={`saved-view-delete-${v.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle>Save this filter combination</DialogTitle>
            <DialogDescription>
              Re-run it later from the Saved Views list — no need to re-pick the filters.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)}
                placeholder='e.g. "Hot UAE Leads — Aleesha team — last 14d"'
                data-testid="save-view-name" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={saveShared} onCheckedChange={(v) => setSaveShared(!!v)} data-testid="save-view-shared" />
              <span>Share with other admins (visible to all super_admins / admins / sales_managers)</span>
            </label>
            <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
              <strong>Dataset:</strong> {dataset} · <strong>Filters:</strong> {filtersCount} · <strong>Split:</strong> {splitByTeam ? 'Yes' : 'No'}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)} data-testid="save-view-cancel">Cancel</Button>
            <Button onClick={saveCurrent} className="bg-violet-600 hover:bg-violet-700" data-testid="save-view-confirm">
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CustomExportBuilder;
